import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { getComicById, getRelatedComics } from '@/mocks/comics';

function RelatedCard({ comic }: { comic: { id: string; title: string; coverImage: string; author: string; rating: number } }) {
  return (
    <Link to={`/comic/${comic.id}`} className="group block" data-product-shop>
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200 mb-2">
        <img
          src={comic.coverImage}
          alt={comic.title}
          title={`${comic.title} - 免费在线看漫画全集`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent pt-6 pb-2 px-2">
          <div className="flex items-center gap-1 text-accent-400">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-star-fill text-xs"></i>
            </div>
            <span className="text-xs font-medium">{comic.rating}</span>
          </div>
        </div>
      </div>
      <h4 className="text-xs font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
        {comic.title}
      </h4>
      <p className="text-[10px] text-foreground-500">{comic.author}</p>
    </Link>
  );
}

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const relatedComics = id ? getRelatedComics(id, 4) : [];

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该漫画可能已下架或不存在</p>
            <Link to="/" className="inline-flex items-center gap-1 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 九妖漫画网免费在线看漫画全集`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0].updateTime,
    dateModified: comic.updateTime,
    image: comic.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '九妖漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.jiuyaomanhua.com/comic/${comic.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 免费在线看漫画全集`}
        description={`${comic.title}漫画全集免费在线阅读，作者${comic.author}。${comic.description}九妖漫画网为您提供${comic.title}高清无删减漫画资源。`}
        keywords={`${comic.title},九妖漫画网,免费在线看,漫画全集,${comic.author},${comic.tags.join(',')}`}
        canonicalUrl={`https://www.jiuyaomanhua.com/comic/${comic.id}`}
        ogType="article"
        breadcrumbs={[
          { label: '首页', href: '/' },
          { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
          { label: comic.title, href: `/comic/${comic.id}` },
        ]}
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </div>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                {comic.category}漫画
              </Link>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </div>
              <span className="text-foreground-700 font-medium">{comic.title}</span>
            </nav>
          </div>

          {/* Comic Detail */}
          <section className="pb-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Cover */}
                <div className="flex-shrink-0 w-full md:w-[280px]">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - 九妖漫画网免费在线看漫画全集`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-sm text-foreground-500">作者：<strong className="text-foreground-700">{comic.author}</strong></span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="flex items-center gap-1 text-sm">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                      <span className="font-medium text-foreground-700">{comic.rating}</span>
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-sm text-foreground-500">{comic.views.toLocaleString()} 次阅读</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'}`}>
                      {comic.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 mb-5">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-md">{tag}</span>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 mb-6">
                    <h2 className="font-heading text-base font-bold text-foreground-900 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/comic/${comic.id}/read/1`}
                      className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      开始阅读
                    </Link>
                    <button className="px-6 py-3 border border-background-300 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap">
                      <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                        <i className="ri-bookmark-line"></i>
                      </div>
                      收藏
                    </button>
                  </div>

                  <div className="flex items-center gap-6 mt-5 text-sm text-foreground-500">
                    <span>更新时间：{comic.updateTime}</span>
                    <span>共 {comic.chapters.length} 话</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Chapter List */}
          <section className="pb-14 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">章节列表</h2>
              <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                <div className="divide-y divide-background-200/50">
                  {comic.chapters.map((ch, index) => (
                    <Link
                      key={ch.id}
                      to={`/comic/${comic.id}/read/${index + 1}`}
                      className="flex items-center justify-between px-5 py-4 hover:bg-background-100 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-foreground-400 font-medium w-12 flex-shrink-0">
                          第{index + 1}话
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors">
                          {ch.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-foreground-400">{ch.pageCount}页</span>
                        <span className="text-xs text-foreground-400 hidden sm:inline">{ch.updateTime}</span>
                        <div className="w-4 h-4 flex items-center justify-center text-foreground-300 group-hover:text-primary-500">
                          <i className="ri-arrow-right-s-line"></i>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="py-14">
              <div className="max-w-[1400px] mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <RelatedCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}